import type { PathWatcherEvent, WatchOptions } from "./types";

interface NodepodLike {
  fs: {
    readFile(path: string): Promise<string | Uint8Array>;
    exists(path: string): Promise<boolean>;
    watch(
      path: string,
      options: { recursive?: boolean },
      cb: (event: "change" | "rename", filename: string | null) => void,
    ): { close(): void };
  };
}

// Nodepod's watcher gives (event, filename). WebContainer's watchPaths calls the
// callback with a single PathWatcherEvent[] argument (one flat batch per tick).
// Dualite's bufferWatchEvents collects calls via (...args) => events.push(args),
// producing Array<[PathWatcherEvent[]]>, then invokes processEventBuffer with that;
// so each watchPaths call must pass exactly one PathWatcherEvent[] as the argument.
export function createWatchPaths(nodepod: NodepodLike) {
  return function watchPaths(
    options: WatchOptions,
    cb: (events: PathWatcherEvent[]) => void,
  ): void {
    // Watch the common root of the include globs (strip the /** suffix).
    const root = options.include[0]?.replace(/\/\*\*.*$/, "") || "/home/project";

    nodepod.fs.watch(root, { recursive: true }, (event, filename) => {
      void (async () => {
        if (!filename) return;
        const path = filename.startsWith("/") ? filename : `${root}/${filename}`;
        if (
          options.exclude?.some((ex) => {
            const stripped = ex.replace(/^\*\*\//, "");
            return path.split("/").some((seg) => seg === stripped);
          })
        ) return;

        const stillExists = await nodepod.fs.exists(path);
        let type: PathWatcherEvent["type"];
        let buffer: Uint8Array | undefined;

        if (!stillExists) {
          type = "remove_file";
        } else {
          type = event === "rename" ? "add_file" : "change";
          if (options.includeContent) {
            const data = await nodepod.fs.readFile(path);
            buffer = typeof data === "string" ? new TextEncoder().encode(data) : data;
          }
        }
        cb([{ type, path, buffer }]);
      })().catch((err) => console.error("[watchPaths]", err));
    });
  };
}
